/**
 * Lazy loading utility with retry logic for chunk load failures.
 *
 * Safari and other browsers' tab suspension can cause chunk loads to fail
 * with status 0 when the network layer is in an unstable state after
 * page restoration. This wrapper adds automatic retry with exponential
 * backoff to handle transient failures.
 *
 * @module platform/utilities/lazy-load-with-retry
 */
import { lazy } from 'react';
import { reloadIfBuildStale } from './stale-build-detector';

/**
 * Default retry configuration
 */
const DEFAULT_CONFIG = {
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 10000,
};

/**
 * Substrings (lowercased) that identify a chunk/dynamic-import load failure.
 *
 * Covers both webpack-style messages and the native dynamic `import()`
 * failures produced by esbuild-built and Stencil-lazy-loaded chunks
 * (e.g. the `wc-chunk-*.js` design system chunks). Each browser phrases
 * the native failure differently:
 *   - Firefox:     "error loading dynamically imported module"
 *   - Chrome/Edge: "Failed to fetch dynamically imported module"
 *   - Safari:      "Importing a module script failed"
 *
 * Exported and consumed by the platform Datadog `beforeSend` filters
 * (`platform/monitoring/Datadog`) so the retry matcher and the noise filters
 * stay in sync. Frozen because it is a shared cross-module contract.
 */
const CHUNK_LOAD_ERROR_MESSAGES = Object.freeze([
  'loading chunk',
  'loading css chunk',
  'error loading dynamically imported module',
  'failed to fetch dynamically imported module',
  'importing a module script failed',
]);

/**
 * Check if an error is a chunk load error
 * @param {Error} error - The error to check
 * @returns {boolean} - True if this is a chunk load error
 */
function isChunkLoadError(error) {
  if (error?.name === 'ChunkLoadError') {
    return true;
  }

  const message = error?.message?.toLowerCase();
  if (!message) {
    return false;
  }

  return CHUNK_LOAD_ERROR_MESSAGES.some(substring =>
    message.includes(substring),
  );
}

/**
 * Calculate delay with exponential backoff and jitter
 * @param {number} attempt - Current attempt number (0-indexed)
 * @param {number} baseDelayMs - Base delay in milliseconds
 * @param {number} maxDelayMs - Maximum delay in milliseconds
 * @returns {number} - Delay in milliseconds
 */
function calculateDelay(attempt, baseDelayMs, maxDelayMs) {
  const exponentialDelay = baseDelayMs * 2 ** attempt;
  const jitter = Math.random() * 0.3 * exponentialDelay; // Add up to 30% jitter
  return Math.min(exponentialDelay + jitter, maxDelayMs);
}

/**
 * Report a terminal chunk-load failure (all retries exhausted) to Datadog RUM
 * as a `custom`-source error, mirroring the wc-loader terminal signal.
 *
 * The platform Datadog `beforeSend` baseline drops the transient
 * `console`-source chunk-load errors emitted on each failed attempt. Web
 * components already report their terminal failures via
 * `DD_RUM.addError(..., { source: 'wc-loader' })`, so those survive the filter;
 * lazy-loaded routes/components had no such authoritative signal. This closes
 * that gap so a genuinely-failed lazy load is never silently filtered out.
 *
 * Reports via the `window.DD_RUM` global written by the Datadog bootstrap, and
 * is a no-op when RUM is unavailable (SSR, local, CI). Avoids optional chaining
 * for test-env transpilation compatibility.
 *
 * @param {Error} error - The terminal error being re-thrown
 */
function reportTerminalChunkLoadError(error) {
  if (
    typeof window !== 'undefined' &&
    window.DD_RUM &&
    typeof window.DD_RUM.addError === 'function'
  ) {
    window.DD_RUM.addError(error, { source: 'lazy-load-with-retry' });
  }
}

/**
 * Attempt to load a module with retries
 * @param {Function} importFn - The dynamic import function
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} baseDelayMs - Base delay between retries
 * @param {number} maxDelayMs - Maximum delay between retries
 * @returns {Promise} - Promise that resolves to the module
 */
async function loadWithRetry(importFn, maxRetries, baseDelayMs, maxDelayMs) {
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await importFn();
    } catch (error) {
      lastError = error;

      // Only retry chunk load errors
      if (!isChunkLoadError(error)) {
        throw error;
      }

      // Don't retry after the last attempt
      if (attempt === maxRetries) {
        // Confirm whether the app is actually out of date (stale deploy)
        // before reporting. A stale build can't be fixed by retrying — the
        // chunk hash genuinely no longer exists on the CDN — so instead of
        // reporting a spurious "broken" alert for a self-recoverable
        // condition, silently reload to fetch the current build (at most
        // once per tab session; isBuildStale fails closed, so a genuinely
        // transient failure is left alone). reloadIfBuildStale never
        // throws, so failures here can't mask the original error.
        // eslint-disable-next-line no-await-in-loop
        const recovered = await reloadIfBuildStale();
        if (!recovered) {
          reportTerminalChunkLoadError(error);
        }
        throw error;
      }

      const delay = calculateDelay(attempt, baseDelayMs, maxDelayMs);

      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

/**
 * Create a lazy component with automatic retry on chunk load failure.
 *
 * Use this instead of React.lazy when loading components that may fail
 * due to network issues, especially on Safari/iOS.
 *
 * @example
 * // Instead of:
 * const MyComponent = lazy(() => import('./MyComponent'));
 *
 * // Use:
 * const MyComponent = lazyWithRetry(() => import('./MyComponent'));
 *
 * @param {Function} importFn - A function that returns a dynamic import promise
 * @param {object} [config] - Optional configuration
 * @param {number} [config.maxRetries=3] - Maximum number of retry attempts
 * @param {number} [config.baseDelayMs=1000] - Base delay between retries (ms)
 * @param {number} [config.maxDelayMs=10000] - Maximum delay between retries (ms)
 * @returns {React.LazyExoticComponent} - A lazy component
 */
export function lazyWithRetry(importFn, config = {}) {
  const { maxRetries, baseDelayMs, maxDelayMs } = {
    ...DEFAULT_CONFIG,
    ...config,
  };

  return lazy(() =>
    loadWithRetry(importFn, maxRetries, baseDelayMs, maxDelayMs),
  );
}

// `loadWithRetry`, `isChunkLoadError`, and `calculateDelay` are exported for
// direct testing of the retry logic without React rendering.
// `CHUNK_LOAD_ERROR_MESSAGES` is a production export shared with the platform
// Datadog `beforeSend` filters (see platform/monitoring/Datadog).
export {
  loadWithRetry,
  isChunkLoadError,
  calculateDelay,
  CHUNK_LOAD_ERROR_MESSAGES,
};

export default lazyWithRetry;
